import { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';

/**
 * Sticky banner shown across the whole app when the device is offline.
 * Mounted once in AppShell so every page surfaces the same message —
 * pages don't have to reinvent the "data didn't load because we're
 * offline" hint individually.
 *
 * Listens to the browser's `online`/`offline` events plus the initial
 * `navigator.onLine` value. We don't try to ping a server here; if a
 * specific request fails for a non-network reason the page still owns
 * surfacing that error inline.
 */
export function OfflineBanner() {
    const [isOnline, setIsOnline] = useState<boolean>(
        () => (typeof navigator === 'undefined' ? true : navigator.onLine),
    );

    useEffect(() => {
        const onOnline = () => setIsOnline(true);
        const onOffline = () => setIsOnline(false);
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        return () => {
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
        };
    }, []);

    if (isOnline) return null;

    return (
        <div
            role="status"
            aria-live="polite"
            className="bg-warning-soft border-b border-warning-border text-warning-text px-4 py-2 text-sm flex items-center gap-2"
        >
            <WifiOff className="w-4 h-4 shrink-0" aria-hidden />
            <span>Network problem — you appear to be offline. Reconnect to continue.</span>
        </div>
    );
}
